import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ChangeCallerIdNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for Change Caller ID node functionality.
 * Handles new caller ID text input and mode (replace/add_front) updates.
 */
export const useChangeCallerIdNode = (
  id: string,
  data: ChangeCallerIdNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const [callerId, setCallerId] = useState(data.newCallerId || '')
  const [error, setError] = useState<string | null>(null)
  const currentMode = data.mode || 'add_front'
  const currentMinimized = data.minimized ?? false

  // Sync local state when node data changes from outside (not from our own updates)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      const newCallerId = data.newCallerId || ''
      setCallerId((prev) => (newCallerId !== prev ? newCallerId : prev))
    }
  }, [data.newCallerId])

  // Read save-time validation errors from node data
  useEffect(() => {
    const validationErrors = data.validationErrors as
      Record<string, string> | undefined
    if (validationErrors?.newCallerId) {
      setError(validationErrors.newCallerId)
    } else {
      setError(null)
    }
  }, [data.validationErrors])

  const handleCallerIdChange = useCallback(
    (newCallerId: string) => {
      // Only update local state - don't update React Flow yet
      isUpdatingRef.current = true
      setCallerId(newCallerId)
      // Clear validation error when user starts typing
      if (error && dataRef.current.validationErrors?.newCallerId) {
        const updatedErrors = { ...dataRef.current.validationErrors }
        delete updatedErrors.newCallerId
        updateNodeData(id, {
          ...dataRef.current,
          validationErrors:
            Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
        })
        setError(null)
      }
    },
    [id, updateNodeData, error],
  )

  const handleCallerIdBlur = useCallback(() => {
    // Update React Flow when user is done typing (on blur)
    updateNodeData(id, {
      ...dataRef.current,
      newCallerId: callerId || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, callerId])

  const handleModeChange = useCallback(
    (mode: 'replace' | 'add_front') => {
      const newCallerIdOption =
        mode === 'replace' ? 'replace_caller' : 'six_char'
      updateNodeData(id, {
        ...dataRef.current,
        mode,
        newCallerIdOption,
      })
    },
    [id, updateNodeData],
  )

  const handleMinimizedChange = useCallback(
    (minimized: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        minimized,
      })
    },
    [id, updateNodeData],
  )

  return {
    currentCallerId: callerId,
    currentMode,
    handleCallerIdChange,
    handleCallerIdBlur,
    handleModeChange,
    currentMinimized,
    handleMinimizedChange,
    error,
    hasData: !!callerId || !!currentMode,
  }
}
